import { ConfigService, getFeatureFlags } from "../services/config-service";
import { CONST_POW_ENDPOINT_V2, FeatureFlagModelBase } from "./feature-flag-model";
import { MODEL_DATA_STATUS } from "./base-model";
import { VoyageImoModel } from "./voyage-imo-model";
import { VoyageImoEventNames } from "./voyage-imo-model-impl";


export const ISWEATHERROUTINGENABLED = "isWeatherRoutingEnabled";
export const ISTRINITYENABLED = "isTrinityEnabled";
export const ISIMMINENTRISKENABLED = "isImminentRiskEnabled";
export const ISOPENOCCEANROUTEENABLED = "isOpenOceanRouteEnabled";
export const ISLOADSTARENABLED = "isLoadstarEnabled";
export const ISHIGHLIGHTSHALLOWSOUNDINGSENABLED = "isHighlightShallowSoundingsEnabled";

export class FeatureFlagModelImpl extends FeatureFlagModelBase {
    private _featureFlags: getFeatureFlags | null = null;
    private _isInitialized: boolean = false;
   
    constructor() {
        super();
        // Set cache lifecycle to 5 minutes
        this.setLifecycleTimeout(5);
        this._initializeDefaults();
        this._initializeAsync();

        // Set up periodic refresh with proper context binding
        setInterval(() => this.getFeatureFlags(), 1 * 60 * 1000);

        // The flags are per vessel (config api: featureflags/enabled/<imo>), so a
        // vessel switch has to re-fetch them for the new IMO instead of serving
        // the previous vessel's cached response.
        VoyageImoModel?.addEventListener(VoyageImoEventNames.ImoUpdated, () => this.refreshFeatureFlags());
    }

    /**
     * Forces a fetch of the feature flags for the current IMO, bypassing the
     * cache lifecycle. The cached response is kept as a fallback until the new
     * one arrives, so a failed request still leaves the last known flags in place.
     */
    async refreshFeatureFlags(): Promise<getFeatureFlags> {
        this._status = MODEL_DATA_STATUS.NOT_READY;
        this._lastFetchedTs = 0;
        return this.getFeatureFlags();
    }

    /**
     * Whether a flag is present in the feature flags response from
     * configapi/v1/config/featureflags/enabled/<imo>.
     *
     * Synchronous, so render paths can consult it directly. It reads the response already fetched
     * (and cached) rather than a mirrored copy, so there is one source of truth; before the first
     * response lands every flag reads as absent, which is why callers re-render on
     * 'featureFlagsUpdated'.
     */
    hasFeatureFlag(flagName: string): boolean {
        return this._featureFlags?.featureFlags?.includes(flagName) === true;
    }

    private _initializeDefaults() {
        // Set default values immediately so main app doesn't get null
        localStorage.setItem(ISWEATHERROUTINGENABLED, "false");
        localStorage.setItem(ISTRINITYENABLED, "false");
        localStorage.setItem(ISIMMINENTRISKENABLED, "false");
        localStorage.setItem(ISOPENOCCEANROUTEENABLED, "false");
        localStorage.setItem(ISLOADSTARENABLED, "false");
        localStorage.setItem(ISHIGHLIGHTSHALLOWSOUNDINGSENABLED, "false");
    }

    private async _initializeAsync() {
        try {
            await this.getFeatureFlags();
            this._isInitialized = true;
            // Dispatch event to notify that initial load is complete
            this.dispatchEvent(new CustomEvent('featureFlagsInitialized'));
        } catch (error) {
            console.error('Failed to initialize feature flags:', error);
            this._isInitialized = true; // Still mark as initialized to prevent hanging
        }
    }

    async getFeatureFlags(): Promise<getFeatureFlags> {
        try {
            // Return cached data if still fresh
            if (this.status === MODEL_DATA_STATUS.COMPLETE && this._featureFlags) {
                return this._featureFlags;
            }

            this.setCacheLifecycle();
            
            const response = await ConfigService.getFeatureFlags();
            
            // Cache the response
            this._featureFlags = response;
            
            // Update localStorage with new values
            const isWeatherRoutingEnabled = response.featureFlags.includes("weather_routing");
            const isTrinityEnabled = response.featureFlags.includes("trinity");
            const isImminentRiskEnabled = response.featureFlags.includes("imminent_risk_parametric_roll_frontend");
            const isOpenOceanRouteEnabled = response.featureFlags.includes("open_ocean_routes")
                || (<any>window)["appenv"]?.featureToggles?.openOceanRoutes === true;
            const isLoadstarEnabled = response.featureFlags.includes("loadstar");
            const isHighlightShallowSoundingsEnabled = response.featureFlags.includes("highlight_shallow_soundings");

            localStorage.setItem(ISWEATHERROUTINGENABLED, isWeatherRoutingEnabled.toString());
            localStorage.setItem(ISTRINITYENABLED, isTrinityEnabled.toString());
            localStorage.setItem(ISIMMINENTRISKENABLED, isImminentRiskEnabled.toString());
            localStorage.setItem(ISOPENOCCEANROUTEENABLED, isOpenOceanRouteEnabled.toString());
            localStorage.setItem(ISLOADSTARENABLED, isLoadstarEnabled.toString());
            localStorage.setItem(ISHIGHLIGHTSHALLOWSOUNDINGSENABLED, isHighlightShallowSoundingsEnabled.toString());
            // const_pow_endpoint_v2 is deliberately not mirrored here: it is read straight off this
            // response through hasFeatureFlag(CONST_POW_ENDPOINT_V2).

            this.setCompleteLifecycle();
            
            // Dispatch event to notify listeners of updates
            this.dispatchEvent(new CustomEvent('featureFlagsUpdated', { 
                detail: { featureFlags: response } 
            }));

            return response;
        } catch (error) {
            console.error('Failed to fetch feature flags:', error);
            
            // Return cached data if available
            if (this._featureFlags) {
                return this._featureFlags;
            }
            
            // Return default structure if no cache available
            return {
                id: 0,
                featureFlags: [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isActive: false
            };
        }
    }

    /**
     * Check if a specific feature flag is enabled
     */
    async isFeatureEnabled(flagName: string): Promise<boolean> {
        try {
            const flags = await this.getFeatureFlags();
            return flags.featureFlags.includes(flagName);
        } catch (error) {
            console.warn(`Failed to check feature flag '${flagName}':`, error);
            return false;
        }
    }

    /**
     * Get current state of initialization
     */
    get isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * Wait for initialization to complete
     */
    async waitForInitialization(): Promise<void> {
        if (this._isInitialized) {
            return Promise.resolve();
        }
        
        return new Promise((resolve) => {
            const handleInit = () => {
                this.removeEventListener('featureFlagsInitialized', handleInit);
                resolve();
            };
            this.addEventListener('featureFlagsInitialized', handleInit);
        });
    }
}
